import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from '@mui/material';
import CustomInputField from '../../../../ui/primitives/CustomInputField';

const maxLength = 250;

const CommentInput = ({
  users,
  comment,
  onCommentChange,
  setTaggedUsers,
}) => {
  const [mentioning, setMentioning] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeUserIndex, setActiveUserIndex] =
    useState(-1);
  const [mentionStart, setMentionStart] =
    useState(null);
  const [cursorPosition, setCursorPosition] =
    useState(null);

  const [dropdownPosition, setDropdownPosition] =
    useState({
      top: 0,
      left: 0,
    });

  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const userMap = useMemo(
    () =>
      new Map(
        users.map((user) => [
          user?.full_name?.trim()?.toLowerCase(),
          user?.id,
        ]),
      ),
    [users],
  );

  useEffect(() => {
    if (
      cursorPosition !== null &&
      textareaRef.current
    ) {
      textareaRef.current.focus();

      textareaRef.current.setSelectionRange(
        cursorPosition,
        cursorPosition,
      );
    }
  }, [cursorPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target)
      ) {
        setMentioning(false);
        setFilteredUsers([]);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside,
      );
    };
  }, []);

  useEffect(() => {
    if (!textareaRef.current || !mentioning)
      return;

    const textarea = textareaRef.current;

    const {
      offsetTop,
      offsetLeft,
      clientHeight,
      clientWidth,
      scrollLeft,
    } = textarea;

    const textBeforeCursor = comment.slice(
      0,
      cursorPosition,
    );

    const cursorLineIndex =
      textBeforeCursor.split('\n').length - 1;

    const lineHeight =
      clientHeight /
      Math.max(
        textBeforeCursor.split('\n').length,
        1,
      );

    const dropdownTop =
      offsetTop +
      lineHeight * cursorLineIndex +
      36;

    const charWidth = 8;
    const dropdownLeft =
      offsetLeft +
      (cursorPosition -
        textBeforeCursor.lastIndexOf(
          '\n',
          cursorPosition,
        ) -
        1) *
        charWidth -
      scrollLeft;

    const dropdownWidth = 180;
    const maxLeft = Math.max(
      0,
      Math.min(
        clientWidth - dropdownWidth,
        dropdownLeft,
      ),
    );

    setDropdownPosition({ top: dropdownTop, left: maxLeft });
  }, [mentioning, cursorPosition, comment]);

  useEffect(() => {
    if (filteredUsers.length > 0) {
      setActiveUserIndex(0);
    }
  }, [filteredUsers]);

  const findMentionBounds = (start) => {
    let end = start;
    while (end < comment.length && !/\s|\n/.test(comment[end])) {
      end++;
    }
    let mentionStartPos = start;
    while (
      mentionStartPos > 0 &&
      !/\s|\n/.test(comment[mentionStartPos - 1]) &&
      comment[mentionStartPos - 1] !== '@'
    ) {
      mentionStartPos--;
    }

    return { start: mentionStartPos, end,
    };
  };

  const getAccountId = (displayName) =>
    userMap.get(displayName.toLowerCase());

  const updateTaggedUsers = (
    updatedComment,
  ) => {
    const currentMentions = new Set();

    const mentionRegex =
      /@\[([\w'_.-]+)\]|@([\w'_.-]+(?:\s+[\w'_.-]+)?)/gi;

    let match;

    while (
      (match =
        mentionRegex.exec(updatedComment)) !==
      null
    ) {
      if (match[1]) {
        currentMentions.add(
          match[1].trim(),
        );
      } else if (match[2]) {
        const displayName = match[2]
          .trim()
          .replace(/_/g, ' ');

        const accountId =
          getAccountId(displayName);

        if (accountId) {
          currentMentions.add(accountId);
        }
      }
    }

    setTaggedUsers([...currentMentions]);
  };

  const handleChange = (_, value) => {
    let updatedValue = value;

    const cursorPos =
      textareaRef.current?.selectionStart ??
      0;

    if (updatedValue.length > maxLength) {
      updatedValue = updatedValue.slice(
        0,
        maxLength,
      );
    }

    onCommentChange(updatedValue);

    setCursorPosition(cursorPos);

    const mentionIndex =
      updatedValue.lastIndexOf(
        '@',
        cursorPos - 1,
      );

    if (mentionIndex > -1) {
      setMentionStart(mentionIndex);

      const mentionText = updatedValue
        .slice(mentionIndex + 1, cursorPos)
        .trim();

      if (mentionText) {
        const filtered = users.filter(
          (user) =>
            user?.full_name
              ?.trim()
              ?.toLowerCase()
              ?.startsWith(
                mentionText.toLowerCase(),
              ),
        );
        setFilteredUsers(filtered);
        setMentioning(filtered.length > 0);
      } else {
        setFilteredUsers(users);
        setMentioning(true);
      }
    } else {
      setMentioning(false);
      setFilteredUsers([]);
    }
    updateTaggedUsers(updatedValue);
  };

  const handleKeyDown = (e) => {
    if (!mentioning) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveUserIndex((prev) =>
        prev < filteredUsers.length - 1
          ? prev + 1
          : 0,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();

      setActiveUserIndex((prev) =>
        prev > 0
          ? prev - 1
          : filteredUsers.length - 1,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeUserIndex >= 0) {
        handleUserSelect(
          filteredUsers[activeUserIndex],
        );
      }
    }
  };

  const handleUserSelect = (user) => {
    const { start } = findMentionBounds(mentionStart);
    const end = findMentionBounds(start).end;
    const username = user?.full_name.trim();
    const isAlreadyTagged = comment.includes(`@${username}`);
    if (isAlreadyTagged) return;
    const newComment =
      comment.substring(0, start) +
      `@${username}` +
      comment.substring(end);
    onCommentChange(newComment);
    updateTaggedUsers(newComment);
    setMentioning(false);
    setActiveUserIndex(-1);
    setCursorPosition(start + username.length + 2);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <CustomInputField
        id="comment-input"
        value={comment}
        onChange={handleChange}
        multiline
        rows={3}
        maxLength={maxLength}
        placeholder="Write a comment..."
        onKeyDown={handleKeyDown}
        inputRef={textareaRef}
      />

      {mentioning &&
        filteredUsers.length > 0 && (
          <Paper
            ref={dropdownRef}
            elevation={4}
            sx={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: 180,
              zIndex: 1500,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <MenuList dense>
              {filteredUsers.map(
                (user, index) => (
                  <MenuItem
                    key={user?.id}
                    selected={
                      index === activeUserIndex
                    }
                    onClick={() =>
                      handleUserSelect(user)
                    }
                  >
                    <Typography variant="body2">
                      {user?.full_name.trim()}
                    </Typography>
                  </MenuItem>
                ),
              )}
            </MenuList>
          </Paper>
        )}
    </Box>
  );
};

export default CommentInput;
